import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Heading,
  Button,
  Card,
  CardBody,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Select,
  useToast,
  HStack,
  Badge,
  IconButton,
  VStack,
  Text,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  InputGroup,
  InputRightElement,
  Spinner,
  useBreakpointValue,
} from '@chakra-ui/react';
import DataTable from 'react-data-table-component';
import { FiEdit, FiTrash2, FiPlus, FiRefreshCw, FiSearch } from 'react-icons/fi';
import { menuCategoryApi } from '../config/api';

const ManageMenuCategory = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [deleteCategory, setDeleteCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const toast = useToast();
  
  const { isOpen: isFormOpen, onOpen: onFormOpen, onClose: onFormClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const cancelRef = React.useRef();

  const [formData, setFormData] = useState({
    MENUCATEGORY_NAME: '',
    MENUCATEGORY_STATUS: 1,
    MENUCATEGORY_TYPE: '',
    MENUCATEGORY_DEV: '',
  });

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const integratedToken = localStorage.getItem('integratedToken');
      if (!integratedToken) {
        toast({
          title: 'Token tidak ditemukan',
          description: 'Silakan login ulang untuk mendapatkan token',
          status: 'warning',
          duration: 3000,
          isClosable: true,
        });
        setLoading(false);
        return;
      }

      const params = {};
      if (searchQuery) {
        params.search = searchQuery;
      }
      if (statusFilter !== '') {
        params.status = statusFilter;
      }

      const response = await menuCategoryApi.get('/list', { params });
      if (response.data.status === 200) {
        setCategories(response.data.data || []);
      } else {
        toast({
          title: 'Error',
          description: response.data.message || 'Gagal mengambil data menu category',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Terjadi kesalahan saat mengambil data',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleCreate = () => {
    setEditingCategory(null);
    setFormData({
      MENUCATEGORY_NAME: '',
      MENUCATEGORY_STATUS: 1,
      MENUCATEGORY_TYPE: '',
      MENUCATEGORY_DEV: '',
    });
    onFormOpen();
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      MENUCATEGORY_NAME: category.MENUCATEGORY_NAME || '',
      MENUCATEGORY_STATUS: category.MENUCATEGORY_STATUS !== undefined ? category.MENUCATEGORY_STATUS : 1,
      MENUCATEGORY_TYPE: category.MENUCATEGORY_TYPE || '',
      MENUCATEGORY_DEV: category.MENUCATEGORY_DEV || '',
    });
    onFormOpen();
  };

  const handleSubmit = async () => {
    if (!formData.MENUCATEGORY_NAME || formData.MENUCATEGORY_NAME.trim() === '') {
      toast({
        title: 'Validasi gagal',
        description: 'Nama Category harus diisi',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      const CREATED_BY = userData.nip || userData.username || null;
      const UPDATED_BY = userData.nip || userData.username || null;

      if (editingCategory) {
        // Update
        const response = await menuCategoryApi.post(`/update/${editingCategory.MENUCATEGORY_ID}`, {
          MENUCATEGORY_NAME: formData.MENUCATEGORY_NAME.trim(),
          MENUCATEGORY_STATUS: formData.MENUCATEGORY_STATUS,
          MENUCATEGORY_TYPE: formData.MENUCATEGORY_TYPE || null,
          MENUCATEGORY_DEV: formData.MENUCATEGORY_DEV || null,
          UPDATED_BY,
        });

        if (response.data.status === 200) {
          toast({
            title: 'Berhasil',
            description: 'Menu category berhasil diupdate',
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
          onFormClose();
          fetchCategories();
        }
      } else {
        // Create
        const response = await menuCategoryApi.post('/', {
          MENUCATEGORY_NAME: formData.MENUCATEGORY_NAME.trim(),
          MENUCATEGORY_STATUS: formData.MENUCATEGORY_STATUS,
          MENUCATEGORY_TYPE: formData.MENUCATEGORY_TYPE || null,
          MENUCATEGORY_DEV: formData.MENUCATEGORY_DEV || null,
          CREATED_BY,
        });

        if (response.data.status === 201) {
          toast({
            title: 'Berhasil',
            description: 'Menu category berhasil dibuat',
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
          onFormClose();
          fetchCategories();
        }
      }
    } catch (error) {
      console.error('Error saving category:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Terjadi kesalahan saat menyimpan data',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleDeleteClick = (category) => {
    setDeleteCategory(category);
    onDeleteOpen();
  };

  const handleDeleteConfirm = async () => {
    if (!deleteCategory) return;

    try {
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      const DELETED_BY = userData.nip || userData.username || null;

      const response = await menuCategoryApi.post(`/delete/${deleteCategory.MENUCATEGORY_ID}`, {
        DELETED_BY,
      });

      if (response.data.status === 200) {
        toast({
          title: 'Berhasil',
          description: 'Menu category berhasil dihapus',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        onDeleteClose();
        setDeleteCategory(null);
        fetchCategories();
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Terjadi kesalahan saat menghapus data',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleSearch = () => {
    fetchCategories();
  };

  const handleReset = () => {
    setSearchQuery('');
    setStatusFilter('');
    setTimeout(() => {
      fetchCategories();
    }, 100);
  };

  const isMobileView = useBreakpointValue({ base: true, md: false });

  // DataTable columns definition
  const columns = useMemo(
    () => [
      {
        name: 'ID',
        selector: (row) => row.MENUCATEGORY_ID,
        sortable: true,
        width: '80px',
        omit: isMobileView,
      },
      {
        name: 'Nama Category',
        selector: (row) => row.MENUCATEGORY_NAME || '-',
        sortable: true,
        cell: (row) => (
          <VStack align="start" spacing={1}>
            <Text fontWeight="600" fontSize={{ base: 'sm', lg: 'md' }}>
              {row.MENUCATEGORY_NAME || '-'}
            </Text>
            {isMobileView && (
              <Text fontSize="xs" color="gray.500">
                ID: {row.MENUCATEGORY_ID}
              </Text>
            )}
          </VStack>
        ),
      },
      {
        name: 'Status',
        selector: (row) => row.MENUCATEGORY_STATUS,
        sortable: true,
        cell: (row) => (
          <Badge
            colorScheme={row.MENUCATEGORY_STATUS === 1 ? 'green' : 'red'}
            fontSize={{ base: 'xs', lg: 'sm' }}
          >
            {row.MENUCATEGORY_STATUS === 1 ? 'ACTIVE' : 'NOT ACTIVE'}
          </Badge>
        ),
      },
      {
        name: 'Type',
        selector: (row) => row.MENUCATEGORY_TYPE || '-',
        sortable: true,
        cell: (row) => (
          <Badge
            colorScheme={row.MENUCATEGORY_TYPE === 'be' ? 'blue' : row.MENUCATEGORY_TYPE === 'fe' ? 'purple' : 'gray'}
            fontSize={{ base: 'xs', lg: 'sm' }}
          >
            {row.MENUCATEGORY_TYPE === 'be' ? 'Back End' : row.MENUCATEGORY_TYPE === 'fe' ? 'Front End' : '-'}
          </Badge>
        ),
      },
      {
        name: 'Development',
        selector: (row) => row.MENUCATEGORY_DEV || '-',
        sortable: true,
        cell: (row) => (
          <Badge
            colorScheme={row.MENUCATEGORY_DEV === 'y' ? 'orange' : row.MENUCATEGORY_DEV === 'n' ? 'green' : 'gray'}
            fontSize={{ base: 'xs', lg: 'sm' }}
          >
            {row.MENUCATEGORY_DEV === 'y' ? 'Iya' : row.MENUCATEGORY_DEV === 'n' ? 'Tidak' : '-'}
          </Badge>
        ),
      },
      {
        name: 'Created By',
        selector: (row) => row.CREATED_BY || '-',
        sortable: true,
        omit: isMobileView,
      },
      {
        name: 'Created Time',
        selector: (row) => row.CREATED_TIME || '-',
        sortable: true,
        cell: (row) => (
          <Text fontSize={{ base: 'xs', lg: 'sm' }}>
            {row.CREATED_TIME ? new Date(row.CREATED_TIME).toLocaleString('id-ID') : '-'}
          </Text>
        ),
        omit: isMobileView,
      },
      {
        name: 'Aksi',
        cell: (row) => (
          <HStack spacing={1} justify="center">
            <IconButton
              icon={<FiEdit />}
              size={{ base: 'xs', lg: 'sm' }}
              colorScheme="blue"
              variant="ghost"
              onClick={() => handleEdit(row)}
              aria-label="Edit"
            />
            <IconButton
              icon={<FiTrash2 />}
              size={{ base: 'xs', lg: 'sm' }}
              colorScheme="red"
              variant="ghost"
              onClick={() => handleDeleteClick(row)}
              aria-label="Delete"
            />
          </HStack>
        ),
        width: '100px',
      },
    ],
    [isMobileView, categories]
  );

  // Filtered data for DataTable
  const filteredData = useMemo(() => {
    return categories;
  }, [categories]);

  return (
    <Box p={{ base: 4, md: 6, lg: 8 }}>
      <VStack spacing={6} align="stretch">
        <HStack justify="space-between" flexWrap="wrap" gap={4}>
          <Heading size="lg">Manage Menu Category</Heading>
          <HStack spacing={2}>
            <Button
              leftIcon={<FiRefreshCw />}
              onClick={fetchCategories}
              isLoading={loading}
              size={{ base: 'sm', md: 'md' }}
            >
              Refresh
            </Button>
            <Button
              leftIcon={<FiPlus />}
              colorScheme="blue"
              onClick={handleCreate}
              size={{ base: 'sm', md: 'md' }}
            >
              Tambah Category
            </Button>
          </HStack>
        </HStack>

        <Card>
          <CardBody>
            <VStack spacing={4} align="stretch">
              {/* Search and Filter */}
              <HStack spacing={4} flexWrap="wrap">
                <InputGroup flex="1" minW="200px">
                  <Input
                    placeholder="Cari nama category..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleSearch();
                      }
                    }}
                  />
                  <InputRightElement>
                    <IconButton
                      icon={<FiSearch />}
                      size="sm"
                      variant="ghost"
                      onClick={handleSearch}
                      aria-label="Search"
                    />
                  </InputRightElement>
                </InputGroup>
                <Select
                  placeholder="Filter Status"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  width={{ base: '100%', md: '200px' }}
                >
                  <option value="1">ACTIVE</option>
                  <option value="0">NOT ACTIVE</option>
                </Select>
                <Button
                  onClick={handleSearch}
                  colorScheme="blue"
                  size={{ base: 'sm', md: 'md' }}
                >
                  Cari
                </Button>
                <Button
                  onClick={handleReset}
                  variant="outline"
                  size={{ base: 'sm', md: 'md' }}
                >
                  Reset
                </Button>
              </HStack>

              {/* DataTable */}
              {loading && categories.length === 0 ? (
                <Box textAlign="center" py={8}>
                  <Spinner size="xl" />
                </Box>
              ) : (
                <DataTable
                  columns={columns}
                  data={filteredData}
                  progressPending={loading}
                  pagination
                  paginationPerPage={10}
                  paginationRowsPerPageOptions={[10, 20, 50, 100]}
                  highlightOnHover
                  pointerOnHover
                  responsive
                  noDataComponent={
                    <Box py={8} textAlign="center">
                      <Text color="gray.500">Tidak ada data menu category</Text>
                    </Box>
                  }
                />
              )}
            </VStack>
          </CardBody>
        </Card>
      </VStack>

      {/* Create/Edit Modal */}
      <Modal isOpen={isFormOpen} onClose={onFormClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {editingCategory ? 'Edit Menu Category' : 'Tambah Menu Category'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Nama Category</FormLabel>
                <Input
                  placeholder="Masukkan nama category"
                  value={formData.MENUCATEGORY_NAME}
                  onChange={(e) =>
                    setFormData({ ...formData, MENUCATEGORY_NAME: e.target.value })
                  }
                />
              </FormControl>
              <FormControl>
                <FormLabel>Status</FormLabel>
                <Select
                  value={formData.MENUCATEGORY_STATUS}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      MENUCATEGORY_STATUS: parseInt(e.target.value),
                    })
                  }
                >
                  <option value={1}>ACTIVE</option>
                  <option value={0}>NOT ACTIVE</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Type</FormLabel>
                <Select
                  placeholder="Pilih Type"
                  value={formData.MENUCATEGORY_TYPE}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      MENUCATEGORY_TYPE: e.target.value,
                    })
                  }
                >
                  <option value="">- Pilih Type -</option>
                  <option value="be">Back End (be)</option>
                  <option value="fe">Front End (fe)</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Development</FormLabel>
                <Select
                  placeholder="Pilih Status Development"
                  value={formData.MENUCATEGORY_DEV}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      MENUCATEGORY_DEV: e.target.value,
                    })
                  }
                >
                  <option value="">- Pilih Status -</option>
                  <option value="y">Iya (Sedang Development)</option>
                  <option value="n">Tidak (Tidak Development)</option>
                </Select>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onFormClose}>
              Batal
            </Button>
            <Button colorScheme="blue" onClick={handleSubmit}>
              {editingCategory ? 'Update' : 'Simpan'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Hapus Menu Category
            </AlertDialogHeader>
            <AlertDialogBody>
              Apakah Anda yakin ingin menghapus menu category "{deleteCategory?.MENUCATEGORY_NAME}"?
              Tindakan ini tidak dapat dibatalkan.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>
                Batal
              </Button>
              <Button colorScheme="red" onClick={handleDeleteConfirm} ml={3}>
                Hapus
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default ManageMenuCategory;

